import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { FormValueType } from '../../hooks/useFormValues';
import { Mutation, useImageUpload } from '../../hooks/useImageUpload';
import { IconButton } from '../IconButton';
import { IconTrash, IconUpload } from '../QuirkIcons';
import { Input } from './Input';
// eslint-disable-next-line css-modules/no-unused-class
import formStyles from './FormFields.module.css';
import styles from './FormImageUpload.module.css';
import { QuirkImage } from '../QuirkImage.js';

const RemoveButton = <RemoveArgType, ResultType>({
	document,
	removeImage,
	setValues,
	fieldName,
	onClick
}: {
	document: ResultType;
	removeImage: Mutation<RemoveArgType, ResultType>;
	setValues: (value: FormValueType<ResultType>) => void;
	fieldName: keyof ResultType;
	onClick?: () => void;
}) => {
	const [trigger] = removeImage();
	const onDelete = () => {
		trigger({ document } as RemoveArgType);
		setValues({ [fieldName as string]: '' } as FormValueType<ResultType>);
		onClick && onClick();
	};
	return <IconButton icon={<IconTrash />} label="Remove Photo" onClick={onDelete} />;
};
export const FormImageUpload = <ArgType, RemoveArgType, ResultType>({
	url,
	fieldName,
	document,
	addImage,
	removeImage,
	label,
	setValues
}: {
	url: string;
	fieldName: keyof ResultType;
	document?: ResultType;
	addImage: Mutation<ArgType, ResultType>;
	removeImage?: Mutation<RemoveArgType, ResultType>;
	label: string;
	setValues: (value: FormValueType<ResultType>) => void;
	noPreview?: boolean;
	noPaste?: boolean;
}) => {
	const { uploadImage } = useImageUpload(document, addImage);
	const [imageError, setImageError] = useState(false);
	const [pasteMode, setPasteMode] = useState(false);
	const onUploadClick = () => {
		inputFile.current.click();
	};

	const pasteClick = () => {
		setPasteMode(true);
	};

	const selectImage = async (e: any) => {
		await uploadImage(e);
		setValues({ [fieldName as string]: document?.[fieldName] } as FormValueType<ResultType>);
	};

	useEffect(() => {
		if (document?.[fieldName]) {
			setImageError(false);
			setValues({ [fieldName as string]: document?.[fieldName] } as FormValueType<ResultType>);
		}
	}, [document, fieldName, document?.[fieldName], setValues]);
	const inputFile = useRef<any>(null);

	return (
		<div>
			{label && <div className={formStyles.label}>{label}</div>}
			<div>
				{document?.[fieldName] && (
					<QuirkImage
						className={classNames(styles.image, { [styles.imageError]: imageError })}
						src={`${url}${document?.[fieldName]}`}
						onError={e => setImageError(true)}
						alt={label}
					/>
				)}
			</div>

			<div className={styles.buttonBar}>
				{removeImage && document && document?.[fieldName] !== '' && (
					<RemoveButton
						setValues={setValues}
						fieldName={fieldName}
						document={document}
						removeImage={removeImage}
						onClick={() => setPasteMode(false)}
					/>
				)}
				<IconButton iconRight icon={<IconUpload />} label="Upload new" onClick={onUploadClick} outline />
				<IconButton iconRight icon={<IconUpload />} label="Paste photo" onClick={pasteClick} />
			</div>

			<input
				type="file"
				name={fieldName as string}
				ref={inputFile}
				style={{ visibility: 'hidden', height: '0px' }}
				onChange={e => selectImage(e)}
			/>
			<div className={classNames(styles.pasteBox, { [styles.hidePasteBox]: !pasteMode })}>
				<Input
					type="text"
					name={fieldName as string}
					onPaste={e => {
						setPasteMode(false);
						uploadImage(e);
					}}
					placeholder="Paste image here"
				/>
			</div>
		</div>
	);
};
